import React from "react";
import { adminApi } from "../utils/apiClient.tsx";
import { Box, Grid } from "@mui/material";
import SkeletonComponent from "../utils/Skeleton.tsx";
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Checkbox, ThemeProvider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import AddLocalUserDialog from "./AddLocalUserDialog.tsx";
import UpdatePasswordDialog from "./UpdatePasswordDialog.tsx";
import CustomSnackbar from '../utils/SnackBarCustomComponent.tsx';
import FullscreenSpinner from "./FullscreenSpinner.tsx";
import { darkTheme } from '../utils/graphUtils.tsx';

interface TableRow {
  id: number;
  username: string;
  nome_cognome: string;
  created_at: string;
  updated_at: string;
  overview: boolean;
  audience: boolean;
  real_time: boolean;
  live: boolean;
  on_demand: boolean;
  musica: boolean;
  advertising: boolean;
  piattaforme: boolean;
  social: boolean;
  uae: boolean;
  impostazioni: boolean;
  session_with_token: boolean;
}

const columnsCheckName = ['Overview', 'Audience', 'Real Time', 'Live', 'On Demand', 'Musica', 'Advertising', 'Piattaforme', 'Social', 'UAE', 'Impostazioni', 'Session with token'];
const permissionFields = columnsCheckName.map((label) => label.replace(/ /g, '_').toLowerCase());

export const SettingsLocalAuth = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState<TableRow[]>([]);
  const [isAddOpen, setIsAddOpen] = React.useState(false);
  const [passwordDialogUser, setPasswordDialogUser] = React.useState<string | null>(null);
  const [isError, setIsError] = React.useState({label: '', message: '', correct: false});
  const [open, setOpen] = React.useState(false);

  // Il backend risponde sempre con HTTP 200: in caso di errore il body ha la forma
  // { data: "<messaggio>" } (vedi custom_http_exception_handler), quindi axios non
  // solleva mai un'eccezione e dobbiamo distinguere errore/successo dalla forma del body.
  const getErrorMessage = (data: any): string | null => {
    if (data && typeof data === 'object' && !Array.isArray(data) && 'data' in data) {
      return data.data;
    }
    return null;
  };

  const getUsers = () => {
    return adminApi.post('local_auth/list', {});
  };

  const getPermissions = () => {
    return adminApi.post('local_auth/permissions', {});
  };

  const loadUsers = () => {
    setIsLoading(true);
    Promise.all([getUsers(), getPermissions()])
      .then(([usersResponse, permissionsResponse]) => {
        const usersError = getErrorMessage(usersResponse.data);
        const permissionsError = getErrorMessage(permissionsResponse.data);
        if (usersError || permissionsError) {
          setOpen(true);
          setIsError({label: 'error', message: usersError || permissionsError, correct: false});
          return;
        }
        const permissionsByUsername = {};
        permissionsResponse.data.forEach((perm) => { permissionsByUsername[perm.username] = perm; });

        const userData: TableRow[] = usersResponse.data.map((el, index) => {
          const permissions = permissionsByUsername[el.username];
          const row: any = { ...el, id: index };
          permissionFields.forEach((field) => { row[field] = permissions ? !!permissions[field] : false; });
          return row;
        });
        setRows(userData);
      })
      .catch(() => {
        setOpen(true);
        setIsError({label: 'error', message: 'Errore durante il caricamento degli utenti', correct: false});
      })
      .finally(() => setIsLoading(false));
  };

  React.useEffect(() => {
    loadUsers();
  }, []);

  const handleAddUser = (data: { username: string; nome_cognome: string; password: string; checkboxes: boolean[] }) => {
    const { username, nome_cognome, password, checkboxes } = data;
    const body: any = { username, nome_cognome, password };
    permissionFields.forEach((field, index) => { body[field] = checkboxes[index]; });

    setIsLoading(true);
    adminApi.post('local_auth/register', body)
      .then((response) => {
        const errorMessage = getErrorMessage(response.data);
        if (errorMessage) {
          setOpen(true);
          setIsError({label: 'error', message: errorMessage, correct: false});
          return;
        }
        setOpen(true);
        setIsError({label: 'correct', message: 'Utente creato con successo', correct: true});
        loadUsers();
      })
      .catch((error) => {
        setOpen(true);
        setIsError({label: 'error', message: error?.response?.data?.detail || 'Errore durante la creazione dell\'utente', correct: false});
      })
      .finally(() => setIsLoading(false));
  };

  const handleUpdatePassword = (username: string, new_password: string) => {
    setIsLoading(true);
    adminApi.post('local_auth/update_password', { username, new_password })
      .then((response) => {
        const errorMessage = getErrorMessage(response.data);
        if (errorMessage) {
          setOpen(true);
          setIsError({label: 'error', message: errorMessage, correct: false});
          return;
        }
        setOpen(true);
        setIsError({label: 'correct', message: 'Password aggiornata con successo', correct: true});
      })
      .catch((error) => {
        setOpen(true);
        setIsError({label: 'error', message: error?.response?.data?.detail || 'Errore durante l\'aggiornamento della password', correct: false});
      })
      .finally(() => setIsLoading(false));
  };

  const handleDelete = (username: string) => {
    setIsLoading(true);
    adminApi.delete('local_auth/users', { data: { username } })
      .then((response) => {
        const errorMessage = getErrorMessage(response.data);
        if (errorMessage) {
          setOpen(true);
          setIsError({label: 'error', message: errorMessage, correct: false});
          return;
        }
        setOpen(true);
        setIsError({label: 'correct', message: 'Utente eliminato con successo', correct: true});
        setRows((prev) => prev.filter((row) => row.username !== username));
      })
      .catch((error) => {
        setOpen(true);
        setIsError({label: 'error', message: error?.response?.data?.detail || 'Errore durante l\'eliminazione dell\'utente', correct: false});
      })
      .finally(() => setIsLoading(false));
  };

  const handleCheckboxChange = (id: number, e, field: string) => {
    let flag = true;
    if (field === 'impostazioni' && !e.target.checked) {
      flag = rows.filter((el) => el.impostazioni).length >= 2;
    }
    if (flag) {
      const updatedRows = rows.map((row) =>
        row.id === id ? { ...row, [field]: e.target.checked, editedUser: true } : row
      );
      setRows(updatedRows);
    }
  };

  const handleSavePermissions = () => {
    const editedRows = rows.filter((el: any) => el.hasOwnProperty('editedUser'));
    if (editedRows.length === 0) return;

    setIsLoading(true);
    const body = editedRows.map((row) => {
      const permissions: any = { username: row.username, nome_cognome: row.nome_cognome };
      permissionFields.forEach((field) => { permissions[field] = row[field]; });
      return permissions;
    });

    adminApi.post('local_auth/update_permissions', body)
      .then((response) => {
        const errorMessage = getErrorMessage(response.data);
        if (errorMessage) {
          setOpen(true);
          setIsError({label: 'error', message: errorMessage, correct: false});
          return;
        }
        setOpen(true);
        setIsError({label: 'correct', message: 'Permessi aggiornati con successo', correct: true});
        loadUsers();
      })
      .catch((error) => {
        setOpen(true);
        setIsError({label: 'error', message: error?.response?.data?.detail || 'Errore durante l\'aggiornamento dei permessi', correct: false});
      })
      .finally(() => setIsLoading(false));
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    { field: 'username', headerName: 'Username', width: 160, editable: false },
    { field: 'nome_cognome', headerName: 'Nome Cognome', width: 180, editable: false },
    ...columnsCheckName.map((el, index) => {
      const field = permissionFields[index];
      return {
        field,
        key: 'check_' + index,
        headerName: el,
        minWidth: 130,
        headerAlign: "center",
        flex: 1,
        resizable: false,
        renderCell: (params) => (
          <div style={{ textAlign: "center" }}>
            <Checkbox key={'check_' + index}
              checked={!!params.row[field]}
              onChange={(e) => handleCheckboxChange(params.row.id, e, field)}
              color="primary"
            />
          </div>
        )
      } as GridColDef;
    }),
    { field: 'created_at', headerName: 'Creato il', width: 180, editable: false },
    { field: 'updated_at', headerName: 'Ultimo aggiornamento', width: 180, editable: false },
    {
      field: 'reset_password',
      headerName: 'Password',
      headerAlign: 'center',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ textAlign: "center" }}>
          <GridActionsCellItem
            icon={<LockResetIcon />}
            label="Aggiorna password"
            onClick={() => setPasswordDialogUser(params.row.username)}
          />
        </div>
      ),
    },
    {
      field: 'delete',
      headerName: 'Elimina',
      headerAlign: 'center',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ textAlign: "center" }}>
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Elimina"
            onClick={() => handleDelete(params.row.username)}
          />
        </div>
      ),
    }
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid container
        direction="row"
        rowGap={2}
        sx={{
          justifyContent: "center",
          alignItems: "stretch"
        }}>

        <Grid item
          container
          direction="row"
          sx={{
            justifyContent: "center",
            alignItems: "stretch",
            height: "stretch"
          }}
        >
          {(rows && rows.length > 0)
            ? <Box sx={{ minHeight: '600px', maxHeight: '1400px', width: '100%' }} >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 25,
                      },
                    },
                  }}
                  disableAutosize
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                />
              </Box>
            : (isLoading ? <SkeletonComponent height='600px' /> : <span>Nessun utente locale presente</span>)
          }
        </Grid>

        <Grid item container className="button-row" direction="row" sx={{justifyContent: "center", alignItems: "stretch"}} gap={2} >
          <button className={"button-query"} onClick={loadUsers}>Aggiorna</button>
          <button className={"button-query"} onClick={handleSavePermissions}>Salva Permessi</button>
          <button className={"button-query"} onClick={() => setIsAddOpen(true)}>Aggiungi Utente</button>
        </Grid>

        <FullscreenSpinner isLoading={isLoading} />
        <CustomSnackbar open={open} setOpen={setOpen} message={isError.message} correct={isError.correct}/>
        <AddLocalUserDialog open={isAddOpen} onClose={() => setIsAddOpen(false)} onSubmit={handleAddUser} dashboard={columnsCheckName} />
        <UpdatePasswordDialog
          open={passwordDialogUser !== null}
          username={passwordDialogUser}
          onClose={() => setPasswordDialogUser(null)}
          onSubmit={handleUpdatePassword}
        />
      </Grid>
    </ThemeProvider>
  )
}
